import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Mic,
  Square,
  Play,
  RotateCcw,
  RotateCw,
  Send,
  Type,
  MessageSquare,
  Flag,
  Loader2,
  Info,
  MoreVertical,
  Pause,
  BookmarkPlus,
  BookOpen,
  StopCircle,
  Trash2,
  Volume2,
  VolumeX,
  Languages,
  ChevronLeft,
} from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import {
  getScenario,
  getSession,
  sendReply,
  finishSession,
  pauseSession,
  resumeSession,
  discardSession,
  translateTurn,
  redoLastAnswer,
  requestHint,
} from "@/lib/api";
import { playTTS, stopAll, unlockAudio, clearCache } from "@/lib/audio";

const supportsMediaRecorder = () =>
  typeof window !== "undefined" && "MediaRecorder" in window && !!navigator?.mediaDevices?.getUserMedia;

// Client-side sentence split mirrors backend heuristics; only used to render
// the per-sentence picker. The server also validates + splits authoritatively.
const splitSentences = (text) => {
  if (!text) return [];
  const parts = text.trim().split(/(?<=[.!?])\s+(?=[A-ZÁÉÍÓÚÑ¿¡])/);
  return parts.map((p) => p.trim()).filter(Boolean);
};

const AUTOPLAY_KEY = "wpe_autoplay";
const getAutoplayPref = () => {
  try {
    const v = localStorage.getItem(AUTOPLAY_KEY);
    return v === null ? true : v === "true";
  } catch (e) {
    return true;
  }
};

export default function Conversation() {
  const { sessionId } = useParams();
  const { lang, tr } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [mode, setMode] = useState(supportsMediaRecorder() ? "voice" : "text");
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [aiResponding, setAiResponding] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [draft, setDraft] = useState("");
  const [showHint, setShowHint] = useState(false);
  // Progressive contextual hint state: fetches on first open, then the
  // learner reveals starter → example one tap at a time.
  const [hintState, setHintState] = useState({
    loading: false,
    forAiTurn: null, // ai turn index the hint targets
    hints: null, // {spanish_coaching, english_starter, english_example}
    revealStarter: false,
    revealExample: false,
    error: null,
  });
  const [slower, setSlower] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [micError, setMicError] = useState(null);
  const [confirmFinish, setConfirmFinish] = useState(false);
  const [autoplay, setAutoplay] = useState(getAutoplayPref);
  const [needsUnlock, setNeedsUnlock] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [busyExit, setBusyExit] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // Translation state — per turn index. `state.status` in {idle,loading,ready,error}.
  // Scoped to the current session in this ref; navigation elsewhere clears it.
  const [translations, setTranslations] = useState({}); // { [turnIdx]: { open, sentenceIdx, fullText, sentenceCount, byKey: {[key]: {status,text,error}} } }

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const scrollRef = useRef(null);
  const lastAutoplayedIdxRef = useRef(-1);
  const sessionScopedRef = useRef({ sessionId: null }); // detect cross-scenario switches
  const pausingRef = useRef(false); // prevents late transcription overwriting cleared draft
  const draftRef = useRef(""); // always-latest draft for cleanup handlers

  // Persist autoplay preference
  useEffect(() => {
    try { localStorage.setItem(AUTOPLAY_KEY, String(autoplay)); } catch (e) { /* ignore */ }
    if (!autoplay) stopAll();
  }, [autoplay]);

  // Keep draft ref in sync so pause handlers capture the freshest value.
  useEffect(() => { draftRef.current = draft; }, [draft]);

  // Load session; resume if paused
  useEffect(() => {
    sessionScopedRef.current = { sessionId };
    lastAutoplayedIdxRef.current = -1;
    pausingRef.current = false;
    setTranslations({}); // never leak translation help across sessions
    (async () => {
      try {
        let s = await getSession(sessionId);
        const wasPaused = s.status === "paused";
        if (wasPaused) {
          s = await resumeSession(sessionId);
        }
        if (sessionScopedRef.current.sessionId !== sessionId) return;
        setSession(s);
        // Restore any deliberately saved unsent draft — do NOT auto-submit.
        if (typeof s.unsent_draft === "string" && s.unsent_draft.length > 0) {
          setDraft(s.unsent_draft);
          draftRef.current = s.unsent_draft;
        }
        const learnerCount = (s.turns || []).filter((t) => t.role === "learner").length;
        if (learnerCount === 0) {
          lastAutoplayedIdxRef.current = -1;
        } else {
          const lastCustomerIdx = (s.turns || []).reduce(
            (acc, t, i) => (t.role === "customer" ? i : acc),
            -1,
          );
          lastAutoplayedIdxRef.current = lastCustomerIdx;
        }
        const sc = await getScenario(s.scenario_id);
        if (sessionScopedRef.current.sessionId !== sessionId) return;
        setScenario(sc);
      } catch (err) {
        toast.error(tr("errorGeneric"));
        navigate("/home");
      }
    })();
    // eslint-disable-next-line
  }, [sessionId]);

  // Auto-scroll on new turns
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [session?.turns?.length]);

  // Autoplay newest AI turn (only if not part of loaded history, autoplay on,
  // and session belongs to current mount).
  useEffect(() => {
    if (!session || !autoplay || mode !== "voice") return;
    const turns = session.turns || [];
    const idx = turns.reduce((acc, t, i) => (t.role === "customer" ? i : acc), -1);
    if (idx <= lastAutoplayedIdxRef.current) return;
    const target = turns[idx];
    if (!target) return;
    lastAutoplayedIdxRef.current = idx;
    (async () => {
      const res = await playTTS({
        text: target.text,
        speed: slower ? 0.75 : 1.0,
        sessionId,
        turnIndex: idx,
      });
      if (res.needsUnlock) setNeedsUnlock(true);
      else if (res.played) setNeedsUnlock(false);
      else if (res.error) toast.info(tr("ttsUnavailable"));
    })();
    // eslint-disable-next-line
  }, [session?.turns?.length, autoplay, mode, slower]);

  // Cleanup on unmount & background handling
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) {
        stopAll();
        try {
          recorderRef.current?.stop?.();
        } catch (e) { /* ignore */ }
        streamRef.current?.getTracks?.().forEach((t) => t.stop());
      }
    };
    const onPageHide = () => stopAll();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      stopAll();
      try {
        recorderRef.current?.stop?.();
      } catch (e) { /* ignore */ }
      streamRef.current?.getTracks?.().forEach((t) => t.stop());
    };
  }, []);

  const learnerTurns = useMemo(
    () => (session?.turns || []).filter((t) => t.role === "learner"),
    [session]
  );
  const maxTurns = 8;
  const turnsUsed = learnerTurns.length;
  const finished = session?.status === "finished";

  // Browser/OS back-gesture guard. On Android/Chrome the swipe-back
  // gesture (and any browser Back button) fires `popstate`, which our
  // visible back-btn cannot intercept. While the session is active we
  // push a sentinel history entry on mount and, on popstate, restore
  // it and open the practice menu — so the learner never loses their
  // place. When finished, we let normal navigation proceed.
  useEffect(() => {
    if (!session || finished) return undefined;
    const sentinel = { __ltMenuGuard: true };
    try {
      window.history.pushState(sentinel, "");
    } catch (_) {
      return undefined;
    }
    const onPop = () => {
      // Re-add the sentinel and open the menu.
      try {
        window.history.pushState(sentinel, "");
      } catch (_) {}
      setMenuOpen(true);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [session?.id, finished]);

  const startRecording = useCallback(async () => {
    setMicError(null);
    stopAll(); // Stop any partner speech so it is not captured by the mic
    if (!supportsMediaRecorder()) {
      setMicError("unsupported");
      setMode("text");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeCandidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/ogg;codecs=opus",
        "audio/mp4",
      ];
      const mime = mimeCandidates.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data && e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        // Discard the raw recording after this handler; we never upload it
        // as a saved artifact and never retain it on disk.
        // Byte floor: below this, opus clips are only ambient air and Whisper
        // reliably hallucinates ("Thank you.", "Bon Appetit.", "Thanks for
        // watching."). The server enforces the same floor as a backstop.
        if (blob.size < 3500) {
          setTranscribing(false);
          toast.info(tr("noSpeechDetected"));
          return;
        }
        // If pause/navigation happened while recording, do NOT auto-transcribe
        // and never overwrite the (possibly saved-and-cleared) draft.
        if (pausingRef.current || sessionScopedRef.current.sessionId !== sessionId) {
          setTranscribing(false);
          toast.info(tr("recordingNotSaved"));
          return;
        }
        setTranscribing(true);
        try {
          const { transcribeBlob } = await import("@/lib/api");
          const { text, warning } = await transcribeBlob(blob);
          setTranscribing(false);
          if (pausingRef.current || sessionScopedRef.current.sessionId !== sessionId) {
            toast.info(tr("recordingNotSaved"));
            return;
          }
          if (warning === "empty_transcript" || !text) {
            toast.info(tr("noSpeechDetected"));
            return;
          }
          setDraft(text);
        } catch (err) {
          setTranscribing(false);
          toast.error(err?.response?.data?.detail || tr("errorGeneric"));
        }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (err) {
      setMicError("denied");
      setMode("text");
    }
  }, [tr]);

  const stopRecording = useCallback(() => {
    try {
      recorderRef.current?.stop?.();
    } catch (e) { /* ignore */ }
    setRecording(false);
  }, []);

  const submitDraft = async () => {
    const text = draft.trim();
    if (!text || processing || aiResponding) return;
    setProcessing(true);
    setAiResponding(true);
    try {
      const updated = await sendReply(sessionId, text, mode === "voice");
      if (sessionScopedRef.current.sessionId !== sessionId) return; // scenario switched
      setSession(updated);
      // Successful commit → clear local draft (backend also cleared unsent_draft).
      setDraft("");
      draftRef.current = "";
      // Autoplay is handled by the effect above via turns.length change.
    } catch (err) {
      // On failure, retain draft for correction/retry.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || tr("errorGeneric");
      toast.error(status === 502 ? tr("aiUnavailable") : detail);
    } finally {
      setProcessing(false);
      setAiResponding(false);
    }
  };

  const doRedoLast = async () => {
    // Rewind the last learner turn + trailing AI reply within the SAME
    // session so the learner can replace their most recent answer. No
    // new session, no new attempt, no inflation of Progress. See backend
    // /sessions/{id}/redo_last_answer for the exact semantics.
    if (processing || aiResponding || finished) return;
    setProcessing(true);
    stopAll();
    try {
      const res = await redoLastAnswer(sessionId);
      if (sessionScopedRef.current.sessionId !== sessionId) return;
      setSession(res.session);
      const restored = res.original_text || "";
      setDraft(restored);
      draftRef.current = restored;
      toast.success(tr("redoAnswerConfirmed"));
    } catch (err) {
      const detail = err?.response?.data?.detail || tr("errorGeneric");
      toast.error(detail);
    } finally {
      setProcessing(false);
    }
  };

  const openHint = async () => {
    // Only fires on explicit learner request (never on page load). Fetches
    // three levels of coaching for the CURRENT AI-partner turn. Subsequent
    // opens for the SAME AI turn are free (cached client-side).
    if (finished || aiResponding) return;
    const turns = session?.turns || [];
    let lastCustomerIdx = -1;
    for (let k = turns.length - 1; k >= 0; k--) {
      if (turns[k].role === "customer") { lastCustomerIdx = k; break; }
    }
    if (lastCustomerIdx < 0) {
      toast.error(tr("errorGeneric"));
      return;
    }
    // If the panel is already open for this AI turn, just close it.
    if (showHint && hintState.forAiTurn === lastCustomerIdx) {
      setShowHint(false);
      return;
    }
    setShowHint(true);
    // Reuse cached hints if we already asked for this turn.
    if (hintState.hints && hintState.forAiTurn === lastCustomerIdx) return;
    setHintState({ loading: true, forAiTurn: lastCustomerIdx, hints: null, revealStarter: false, revealExample: false, error: null });
    try {
      const res = await requestHint(sessionId);
      if (sessionScopedRef.current.sessionId !== sessionId) return;
      setHintState({
        loading: false,
        forAiTurn: res.ai_turn_index ?? lastCustomerIdx,
        hints: res.hints || null,
        revealStarter: false,
        revealExample: false,
        error: null,
      });
    } catch (err) {
      setHintState((s) => ({ ...s, loading: false, error: err?.response?.data?.detail || tr("errorGeneric") }));
    }
  };

  // P1-4: Hint freshness — dismiss any prior hint the moment a new AI
  // turn arrives so it can never leak across turns. Opening help again
  // will fetch help for the NEW AI turn only.
  const customerTurnCount = (session?.turns || []).filter((t) => t.role === "customer").length;
  useEffect(() => {
    setShowHint(false);
    setHintState({ loading: false, forAiTurn: null, hints: null, revealStarter: false, revealExample: false, error: null });
  }, [customerTurnCount]);

  const revealHintStarter = () => setHintState((s) => ({ ...s, revealStarter: true }));
  const revealHintExample = () => setHintState((s) => ({ ...s, revealStarter: true, revealExample: true }));
  const fillDraftFromExample = () => {
    const ex = hintState.hints?.english_example || "";
    if (!ex) return;
    setDraft(ex);
    draftRef.current = ex;
  };

  const replayText = async (text, idx) => {
    unlockAudio();
    const res = await playTTS({
      text,
      speed: slower ? 0.75 : 1.0,
      sessionId,
      turnIndex: idx,
    });
    if (res.needsUnlock) setNeedsUnlock(true);
    else if (res.error) toast.info(tr("ttsUnavailable"));
    else if (res.played) setNeedsUnlock(false);
  };

  const translationKey = (sentenceIdx) => (sentenceIdx == null ? "full" : `s${sentenceIdx}`);

  const requestTranslation = async (turnIdx, sentenceIdx) => {
    stopAll(); // stop English audio so the learner can read the translation
    const key = translationKey(sentenceIdx);
    setTranslations((prev) => {
      const cur = prev[turnIdx] || { open: true, sentenceIdx: sentenceIdx ?? null, byKey: {} };
      return {
        ...prev,
        [turnIdx]: {
          ...cur,
          open: true,
          sentenceIdx: sentenceIdx ?? null,
          byKey: { ...cur.byKey, [key]: { status: "loading" } },
        },
      };
    });
    try {
      const r = await translateTurn(sessionId, turnIdx, sentenceIdx);
      if (sessionScopedRef.current.sessionId !== sessionId) return; // discard stale
      setTranslations((prev) => {
        const cur = prev[turnIdx] || { open: true, sentenceIdx: sentenceIdx ?? null, byKey: {} };
        return {
          ...prev,
          [turnIdx]: {
            ...cur,
            open: true,
            sentenceIdx: sentenceIdx ?? null,
            sentenceCount: r.sentence_count,
            byKey: {
              ...cur.byKey,
              [key]: { status: "ready", text: r.translation_es, source: r.source_en },
            },
          },
        };
      });
    } catch (err) {
      if (sessionScopedRef.current.sessionId !== sessionId) return;
      setTranslations((prev) => {
        const cur = prev[turnIdx] || { open: true, sentenceIdx: sentenceIdx ?? null, byKey: {} };
        return {
          ...prev,
          [turnIdx]: {
            ...cur,
            open: true,
            sentenceIdx: sentenceIdx ?? null,
            byKey: {
              ...cur.byKey,
              [key]: { status: "error", error: err?.response?.data?.detail || "" },
            },
          },
        };
      });
    }
  };

  const toggleTranslation = (turnIdx) => {
    const cur = translations[turnIdx];
    if (cur?.open) {
      setTranslations((prev) => ({ ...prev, [turnIdx]: { ...cur, open: false } }));
    } else if (cur && cur.byKey && Object.keys(cur.byKey).length > 0) {
      // Re-open with the previously requested selection (cache hit; no new call)
      setTranslations((prev) => ({ ...prev, [turnIdx]: { ...cur, open: true } }));
    } else {
      requestTranslation(turnIdx, null);
    }
  };

  const pickSentenceTranslation = (turnIdx, sentenceIdx) => {
    const cur = translations[turnIdx];
    const key = translationKey(sentenceIdx);
    if (cur?.byKey?.[key]?.status === "ready") {
      setTranslations((prev) => ({
        ...prev,
        [turnIdx]: { ...cur, open: true, sentenceIdx: sentenceIdx },
      }));
      return;
    }
    requestTranslation(turnIdx, sentenceIdx);
  };

  const enableAudioTap = async () => {
    unlockAudio();
    // Attempt to re-play the last customer turn now that we have a user gesture.
    const turns = session?.turns || [];
    const idx = turns.reduce((acc, t, i) => (t.role === "customer" ? i : acc), -1);
    if (idx < 0) {
      setNeedsUnlock(false);
      return;
    }
    const res = await playTTS({
      text: turns[idx].text,
      speed: slower ? 0.75 : 1.0,
      sessionId,
      turnIndex: idx,
    });
    if (res.played) setNeedsUnlock(false);
  };

  const doFinish = async () => {
    if (finishing) return; // Prevent duplicate taps
    setFinishing(true);
    stopAll();
    try {
      await finishSession(sessionId);
      navigate(`/feedback/${sessionId}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr("errorGeneric"));
      setFinishing(false);
    }
  };

  // When the session is already finished (e.g. AI ended it with [END]),
  // the finish button should take the learner straight to the review
  // screen without another confirm step or re-billing.
  const doGoToReview = () => {
    if (finishing) return;
    stopAll();
    navigate(`/feedback/${sessionId}`);
  };

  // --- Session menu actions ---
  const doPauseGoHome = async () => {
    if (busyExit) return;
    setBusyExit(true);
    pausingRef.current = true;
    stopAll();
    try {
      recorderRef.current?.stop?.();
    } catch (e) { /* ignore */ }
    try {
      streamRef.current?.getTracks?.().forEach((t) => t.stop());
    } catch (e) { /* ignore */ }
    try {
      // Capture the current bounded draft (including intentional empty)
      // and rely on backend save-consent gating (signed-in => durable).
      await pauseSession(sessionId, !!user, draftRef.current || "");
      clearCache();
      navigate("/home");
    } catch (err) {
      pausingRef.current = false;
      toast.error(err?.response?.data?.detail || tr("errorGeneric"));
      setBusyExit(false);
    }
  };

  const doPauseChooseAnother = async () => {
    if (busyExit) return;
    setBusyExit(true);
    pausingRef.current = true;
    stopAll();
    try {
      recorderRef.current?.stop?.();
    } catch (e) { /* ignore */ }
    try {
      streamRef.current?.getTracks?.().forEach((t) => t.stop());
    } catch (e) { /* ignore */ }
    try {
      await pauseSession(sessionId, !!user, draftRef.current || "");
      clearCache();
      navigate("/practice");
    } catch (err) {
      pausingRef.current = false;
      toast.error(err?.response?.data?.detail || tr("errorGeneric"));
      setBusyExit(false);
    }
  };

  const doDiscard = async () => {
    if (busyExit) return;
    setBusyExit(true);
    stopAll();
    try {
      await discardSession(sessionId);
      clearCache();
      navigate("/home");
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr("errorGeneric"));
      setBusyExit(false);
    }
  };

  if (!session || !scenario) return <AppShell hideNav={!user} />;

  return (
    <AppShell hideNav hideHeader>
      <section
        className="flex flex-col h-[100dvh] max-h-[100dvh]"
        data-testid="conversation-screen"
      >
        {/* Top */}
        <div
          className="px-3 py-2.5 flex items-center gap-2 border-b border-[rgb(var(--wpe-border))] bg-[rgb(var(--wpe-page))] sticky top-0 z-10"
        >
          <button
            type="button"
            onClick={() => (finished ? navigate("/home") : setMenuOpen(true))}
            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-full text-[12px] font-semibold text-[rgb(var(--wpe-slate))] hover:bg-[rgb(var(--wpe-subtle))] min-h-[36px]"
            data-testid="back-btn"
            aria-label={tr("backCta")}
          >
            <ChevronLeft size={16} /> {tr("backCta")}
          </button>
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            disabled={finished}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[12px] font-semibold wpe-chip-gold disabled:opacity-40 min-h-[36px]"
            data-testid="pause-btn"
            aria-label={tr("pauseCta")}
          >
            <Pause size={14} /> {tr("pauseCta")}
          </button>
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            className="wpe-ghost text-[12px]"
            data-testid="session-menu-btn"
            aria-label={tr("sessionMenu")}
          >
            <MoreVertical size={16} />
          </button>
          <div className="text-[11px] text-[rgb(var(--wpe-muted))] font-semibold text-center flex-1 mx-1 truncate">
            {lang === "en" ? scenario.title_en : scenario.title_es} · {turnsUsed}/{maxTurns}
          </div>
          <button
            type="button"
            onClick={() => setAutoplay((v) => !v)}
            className={`text-[11px] font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1 ${
              autoplay
                ? "bg-[rgb(var(--wpe-teal-subtle))] text-[rgb(var(--wpe-teal-hover))]"
                : "text-[rgb(var(--wpe-slate))]"
            }`}
            data-testid="autoplay-toggle-btn"
            aria-pressed={autoplay}
            aria-label={autoplay ? tr("autoVoiceOn") : tr("autoVoiceOff")}
            title={autoplay ? tr("autoVoiceOn") : tr("autoVoiceOff")}
          >
            {autoplay ? <Volume2 size={12} /> : <VolumeX size={12} />}
            {tr("autoVoice")}
          </button>
        </div>

        {/* Autoplay-unlock prompt (browser autoplay policy) */}
        {needsUnlock && autoplay && mode === "voice" && !finished && (
          <button
            type="button"
            onClick={enableAudioTap}
            className="wpe-hint text-[12px] mx-4 mt-2 text-left"
            data-testid="autoplay-unlock-btn"
          >
            <Info size={12} className="inline mr-1" />
            {tr("tapToEnableAudio")}
          </button>
        )}

        {/* Transcript */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
          data-testid="transcript"
        >
          {(() => {
            const turns = session.turns || [];
            let lastLearnerIdx = -1;
            for (let k = turns.length - 1; k >= 0; k--) {
              if (turns[k].role === "learner") { lastLearnerIdx = k; break; }
            }
            const canRedo = !finished && lastLearnerIdx >= 0 && !processing && !aiResponding;
            return turns.map((turn, i) => (
            <div key={i} className={turn.role === "learner" ? "flex flex-col items-end" : ""}>
              <div className={turn.role === "learner" ? "wpe-bubble-learner" : "wpe-bubble-customer"} data-testid={`turn-${turn.role}-${i}`}>
                <div className="text-[10px] uppercase tracking-wider opacity-70 mb-0.5">
                  {turn.role === "customer" ? tr("aiCustomer") : tr("youEmployee")}
                </div>
                <div className="text-[14px] leading-snug">{turn.text}</div>
                {turn.role === "customer" && (
                  <div className="mt-2 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => replayText(turn.text, i)}
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-[rgb(var(--wpe-teal-hover))]"
                      data-testid={`replay-audio-btn-${i}`}
                    >
                      <Play size={12} /> {tr("listen")}
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleTranslation(i)}
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-[rgb(var(--wpe-blue-hover))]"
                      data-testid={`translate-btn-${i}`}
                      aria-expanded={!!translations[i]?.open}
                    >
                      <Languages size={12} />
                      {translations[i]?.open ? tr("translateHide") : tr("translateBtn")}
                    </button>
                  </div>
                )}
                {turn.role === "customer" && translations[i]?.open && (
                  <TranslationPanel
                    turnIdx={i}
                    sourceText={turn.text}
                    state={translations[i]}
                    onPickSentence={pickSentenceTranslation}
                    onRetry={requestTranslation}
                    tr={tr}
                  />
                )}
              </div>
              {turn.role === "learner" && i === lastLearnerIdx && canRedo && (
                <button
                  type="button"
                  onClick={doRedoLast}
                  className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-[rgb(var(--wpe-coral-hover))] hover:underline"
                  data-testid="redo-last-answer-btn"
                  title={tr("redoAnswerHint")}
                >
                  <RotateCw size={12} /> {tr("redoAnswerCta")}
                </button>
              )}
            </div>
          ));
          })()}
          {aiResponding && (
            <div className="flex items-center gap-2 text-[12px] text-[rgb(var(--wpe-muted))]" data-testid="ai-responding">
              <Loader2 size={14} className="animate-spin" /> {tr("responding")}
            </div>
          )}
        </div>

        {/* Input area */}
        <div className="border-t border-[rgb(var(--wpe-border))] bg-[rgb(var(--wpe-page))] px-4 py-3 space-y-2">
          {showHint && (
            <div className="wpe-card wpe-card-gold text-[12px] space-y-2" data-testid="hint-panel">
              {hintState.loading && (
                <div className="flex items-center gap-2 text-[rgb(var(--wpe-muted))]">
                  <Loader2 size={12} className="animate-spin" /> {tr("hintLoading")}
                </div>
              )}
              {hintState.error && (
                <div className="text-[rgb(var(--wpe-danger))]" data-testid="hint-error">{hintState.error}</div>
              )}
              {hintState.hints && (
                <>
                  <div data-testid="hint-coaching">
                    <div className="wpe-eyebrow-gold mb-1">{tr("hintCoachingLabel")}</div>
                    <div className="text-[13px] text-[rgb(var(--wpe-navy))] leading-snug">
                      {hintState.hints.spanish_coaching || tr("errorGeneric")}
                    </div>
                  </div>
                  {!hintState.revealStarter ? (
                    <button
                      type="button"
                      onClick={revealHintStarter}
                      className="wpe-secondary text-[12px] py-1.5 min-h-[36px]"
                      data-testid="hint-reveal-starter-btn"
                    >
                      {tr("hintStarterCta")}
                    </button>
                  ) : (
                    <div data-testid="hint-starter">
                      <div className="wpe-eyebrow-teal mb-1">{tr("hintStarterCta")}</div>
                      <div className="font-semibold text-[rgb(var(--wpe-navy))]">
                        {hintState.hints.english_starter}
                      </div>
                    </div>
                  )}
                  {hintState.revealStarter && !hintState.revealExample && (
                    <button
                      type="button"
                      onClick={revealHintExample}
                      className="wpe-ghost text-[12px] py-1.5 min-h-[36px]"
                      data-testid="hint-reveal-example-btn"
                    >
                      {tr("hintExampleCta")}
                    </button>
                  )}
                  {hintState.revealExample && (
                    <div data-testid="hint-example">
                      <div className="wpe-eyebrow-coral mb-1">{tr("hintExampleCta")}</div>
                      <div className="italic text-[rgb(var(--wpe-navy))]">
                        {hintState.hints.english_example}
                      </div>
                      <button
                        type="button"
                        onClick={fillDraftFromExample}
                        className="mt-2 wpe-ghost text-[11px] font-semibold text-[rgb(var(--wpe-coral-hover))]"
                        data-testid="hint-fill-draft-btn"
                      >
                        {tr("hintFillDraftCta")}
                      </button>
                      <div className="mt-1 text-[10px] text-[rgb(var(--wpe-muted))]" data-testid="hint-guided-note">
                        {tr("hintGuidedNote")}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {micError === "denied" && (
            <div className="wpe-hint text-[12px]" data-testid="mic-denied-msg">{tr("micDenied")}</div>
          )}
          {micError === "unsupported" && (
            <div className="wpe-hint text-[12px]" data-testid="mic-unsupported-msg">{tr("micUnsupported")}</div>
          )}

          {!finished && (
            <>
              {mode === "voice" ? (
                <div className="flex flex-col items-center gap-2">
                  <button
                    type="button"
                    aria-label={recording ? tr("stop") : tr("micTap")}
                    className={`wpe-mic-btn ${recording ? "recording" : ""}`}
                    onClick={recording ? stopRecording : startRecording}
                    disabled={transcribing || processing || aiResponding}
                    data-testid="mic-speak-btn"
                  >
                    {recording ? <Square size={28} /> : <Mic size={30} />}
                  </button>
                  <div className="text-[11px] text-[rgb(var(--wpe-muted))]" data-testid="mic-state">
                    {recording
                      ? tr("recording")
                      : transcribing
                      ? tr("transcribing")
                      : tr("micTap")}
                  </div>
                </div>
              ) : null}

              {(mode === "text" || draft) && (
                <div className="space-y-2">
                  <textarea
                    className="wpe-input min-h-[70px] resize-none"
                    placeholder={tr("typeAnswer")}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value.slice(0, 1500))}
                    data-testid="learner-text-input"
                  />
                  <div className="text-[10px] text-[rgb(var(--wpe-muted))]" data-testid="review-hint">
                    {tr("reviewTranscript")}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setMode((m) => (m === "voice" ? "text" : "voice"))}
                  className="wpe-ghost text-[12px]"
                  data-testid="text-mode-btn"
                >
                  {mode === "voice" ? (
                    <>
                      <Type size={14} /> {tr("switchToText")}
                    </>
                  ) : (
                    <>
                      <Mic size={14} /> {tr("switchToVoice")}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setSlower((v) => !v)}
                  className={`text-[11px] font-semibold px-2 py-1 rounded-full ${
                    slower
                      ? "bg-[rgb(var(--wpe-teal-subtle))] text-[rgb(var(--wpe-teal-hover))]"
                      : "text-[rgb(var(--wpe-slate))]"
                  }`}
                  data-testid="speed-toggle-btn"
                  aria-pressed={slower}
                >
                  {slower ? tr("speakSlow") : tr("speakNormal")}
                </button>
                <button
                  type="button"
                  onClick={openHint}
                  disabled={aiResponding || finished}
                  className="wpe-ghost text-[12px]"
                  data-testid="hint-toggle-btn"
                >
                  <MessageSquare size={14} /> {showHint ? tr("hintOff") : tr("hint")}
                </button>
                <button
                  type="button"
                  onClick={submitDraft}
                  disabled={!draft.trim() || processing || aiResponding}
                  className="wpe-cta text-[13px] flex-1 min-h-[44px]"
                  data-testid="submit-reply-btn"
                >
                  {processing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {tr("submit")}
                </button>
              </div>
            </>
          )}

          {finished && (
            <div
              className="wpe-hint text-[12px] text-center font-semibold"
              data-testid="session-finished-note"
            >
              {tr("finishedBadge")}
            </div>
          )}

          <button
            type="button"
            onClick={finished ? doGoToReview : () => setConfirmFinish(true)}
            className={`${finished ? "wpe-cta wpe-cta-coral" : "wpe-secondary"} w-full text-[13px]`}
            disabled={finishing}
            data-testid="finish-convo-btn"
          >
            {finishing ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Flag size={14} />
            )}
            {finished ? tr("goToReviewCta") : tr("finish")}
          </button>
        </div>

        {/* Session menu sheet */}
        {menuOpen && (
          <div
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center px-4"
            data-testid="session-menu-sheet"
            onClick={() => setMenuOpen(false)}
          >
            <div
              className="wpe-card w-full max-w-md space-y-2"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-[15px] font-semibold text-[rgb(var(--wpe-navy))]">
                {tr("sessionMenu")}
              </h3>
              <MenuAction
                icon={<BookmarkPlus size={16} />}
                title={tr("savePauseTitle")}
                hint={tr("savePauseHint")}
                onClick={doPauseGoHome}
                disabled={busyExit}
                testid="menu-save-pause-btn"
              />
              <MenuAction
                icon={<BookOpen size={16} />}
                title={tr("pauseChooseOtherTitle")}
                hint={tr("pauseChooseOtherHint")}
                onClick={doPauseChooseAnother}
                disabled={busyExit}
                testid="menu-pause-choose-btn"
              />
              <MenuAction
                icon={<StopCircle size={16} />}
                title={tr("endPracticeTitle")}
                hint={tr("endPracticeHint")}
                onClick={() => { setMenuOpen(false); setConfirmFinish(true); }}
                disabled={busyExit}
                testid="menu-end-btn"
              />
              <MenuAction
                icon={<Trash2 size={16} />}
                title={tr("discardTitle")}
                hint={tr("discardHint")}
                onClick={() => { setMenuOpen(false); setConfirmDiscard(true); }}
                danger
                disabled={busyExit}
                testid="menu-discard-btn"
              />
              {!user && (
                <div className="wpe-hint text-[11px]" data-testid="menu-guest-note">
                  {tr("guestPauseNote")}
                </div>
              )}
              <button
                type="button"
                className="wpe-secondary w-full"
                onClick={() => setMenuOpen(false)}
                data-testid="menu-cancel-btn"
              >
                {tr("cancelExit")}
              </button>
            </div>
          </div>
        )}

        {confirmFinish && (
          <div
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-6"
            data-testid="finish-confirm-modal"
          >
            <div className="wpe-card max-w-sm w-full space-y-4">
              <h3 className="text-[15px] font-semibold text-[rgb(var(--wpe-navy))]">
                {tr("finishConfirm")}
              </h3>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="wpe-secondary flex-1"
                  onClick={() => setConfirmFinish(false)}
                  data-testid="finish-cancel-btn"
                >
                  {tr("no")}
                </button>
                <button
                  type="button"
                  className="wpe-cta flex-1"
                  onClick={doFinish}
                  disabled={finishing}
                  data-testid="finish-confirm-btn"
                >
                  {finishing ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                  {tr("finishConfirmYes")}
                </button>
              </div>
            </div>
          </div>
        )}

        {confirmDiscard && (
          <div
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-6"
            data-testid="discard-confirm-modal"
          >
            <div className="wpe-card max-w-sm w-full space-y-4">
              <h3 className="text-[15px] font-semibold text-[rgb(var(--wpe-navy))]">
                {tr("discardConfirm")}
              </h3>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="wpe-secondary flex-1"
                  onClick={() => setConfirmDiscard(false)}
                  data-testid="discard-cancel-btn"
                >
                  {tr("cancelExit")}
                </button>
                <button
                  type="button"
                  className="wpe-cta flex-1 !bg-[rgb(var(--wpe-danger))]"
                  onClick={doDiscard}
                  disabled={busyExit}
                  data-testid="discard-confirm-btn"
                >
                  <Trash2 size={14} /> {tr("yes")}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </AppShell>
  );
}

const MenuAction = ({ icon, title, hint, onClick, danger, disabled, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`w-full text-left rounded-lg p-3 border transition flex items-start gap-2 ${
      danger
        ? "border-[rgb(var(--wpe-border))] hover:border-[rgb(var(--wpe-danger))]"
        : "border-[rgb(var(--wpe-border))] hover:border-[rgb(var(--wpe-teal))]"
    } ${disabled ? "opacity-60" : ""}`}
    data-testid={testid}
  >
    <span className={`mt-0.5 shrink-0 ${danger ? "text-[rgb(var(--wpe-danger))]" : "text-[rgb(var(--wpe-teal))]"}`}>
      {icon}
    </span>
    <div className="flex-1">
      <div className="text-[13px] font-semibold text-[rgb(var(--wpe-navy))]">{title}</div>
      <div className="text-[11px] text-[rgb(var(--wpe-muted))]">{hint}</div>
    </div>
  </button>
);

const TranslationPanel = ({ turnIdx, sourceText, state, onPickSentence, onRetry, tr }) => {
  const sentences = splitSentences(sourceText);
  const selectedIdx = state?.sentenceIdx == null ? null : state.sentenceIdx;
  const key = selectedIdx == null ? "full" : `s${selectedIdx}`;
  const cur = state?.byKey?.[key];
  return (
    <div
      className="mt-2 rounded-lg border border-[rgb(var(--wpe-teal-subtle))] bg-[rgb(var(--wpe-teal-subtle))]/40 p-2 space-y-1.5"
      data-testid={`translation-panel-${turnIdx}`}
      role="region"
      aria-label={tr("translatePanelTitle")}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-[rgb(var(--wpe-teal-hover))] font-semibold">
          {tr("translatePanelTitle")}
        </span>
        {sentences.length > 1 && (
          <span className="text-[10px] text-[rgb(var(--wpe-muted))]">{tr("translateSelectSentence")}</span>
        )}
      </div>
      {sentences.length > 1 && (
        <div className="flex flex-wrap gap-1" data-testid={`translation-sentence-picker-${turnIdx}`}>
          <button
            type="button"
            onClick={() => onPickSentence(turnIdx, null)}
            className={`text-[10px] px-2 py-0.5 rounded-full border ${selectedIdx == null ? "bg-[rgb(var(--wpe-teal))] text-white border-[rgb(var(--wpe-teal))]" : "bg-white text-[rgb(var(--wpe-slate))] border-[rgb(var(--wpe-border))]"}`}
            data-testid={`translation-sentence-full-${turnIdx}`}
          >
            {tr("translateFullMessage")}
          </button>
          {sentences.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onPickSentence(turnIdx, i)}
              className={`text-[10px] px-2 py-0.5 rounded-full border max-w-[220px] truncate ${selectedIdx === i ? "bg-[rgb(var(--wpe-teal))] text-white border-[rgb(var(--wpe-teal))]" : "bg-white text-[rgb(var(--wpe-slate))] border-[rgb(var(--wpe-border))]"}`}
              title={s}
              data-testid={`translation-sentence-${turnIdx}-${i}`}
            >
              {i + 1}. {s.slice(0, 30)}{s.length > 30 ? "…" : ""}
            </button>
          ))}
        </div>
      )}
      {cur?.status === "loading" && (
        <div className="text-[11px] text-[rgb(var(--wpe-slate))] flex items-center gap-1" data-testid={`translation-loading-${turnIdx}`}>
          <Loader2 size={12} className="animate-spin" /> {tr("translateLoading")}
        </div>
      )}
      {cur?.status === "ready" && (
        <div className="text-[13px] text-[rgb(var(--wpe-navy))]" data-testid={`translation-text-${turnIdx}`}>
          {cur.text}
        </div>
      )}
      {cur?.status === "error" && (
        <button
          type="button"
          onClick={() => onRetry(turnIdx, selectedIdx)}
          className="text-[11px] text-[rgb(var(--wpe-danger))] underline"
          data-testid={`translation-retry-${turnIdx}`}
        >
          {tr("translateError")}
        </button>
      )}
      <div className="text-[10px] text-[rgb(var(--wpe-muted))]">{tr("translateAiNote")}</div>
    </div>
  );
};
